import React, { useCallback, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import PropTypes from 'prop-types';

// The "Contact Us" button opens the beehiiv newsletter/contact popup. The
// popup form (configured in the beehiiv dashboard) is set to trigger on a
// `click` of the element matching `#contact-us`, so this component must be
// the only element on a page with that id.
//
// Why this component re-injects the loader script instead of loading it once:
// beehiiv's loader.js is written for static pages. When it runs, it does a
// single `querySelectorAll('#contact-us')`, attaches a one-shot click
// listener to whatever it finds, and never looks again. In this React app the
// button is unmounted and re-created on every route change, and the one-shot
// listener removes itself after the first click. Loading the script only once
// therefore meant the button worked exactly once per full page load, on
// whichever page the script happened to initialize, and was dead everywhere
// else until the browser was refreshed.
//
// Re-adding the script tag makes the loader run again (scripts re-execute on
// each insertion; the browser serves it from cache) and bind a fresh listener
// to the button currently in the DOM. We do that on every mount and again
// after every click.

const BEEHIIV_FORM_ID = 'db2cca5b-1c68-4b0b-83d8-dd3da1397a83';
const BEEHIIV_LOADER_SRC = 'https://subscribe-forms.beehiiv.com/v3/loader.js';

export const CONTACT_US_TRIGGER_ID = 'contact-us';

const armBeehiivPopup = () => {
  document
    .querySelectorAll(`script[data-beehiiv-form="${BEEHIIV_FORM_ID}"]`)
    .forEach((el) => el.remove());

  const script = document.createElement('script');
  script.src = BEEHIIV_LOADER_SRC;
  script.setAttribute('data-beehiiv-form', BEEHIIV_FORM_ID);
  script.async = true;
  document.body.appendChild(script);
};

const ContactUsButton = ({ children, className = '' }) => {
  useEffect(() => {
    armBeehiivPopup();
  }, []);

  const handleClick = useCallback(() => {
    // The loader's own listener on this element has already fired (it is
    // bound directly to the element, so it runs before React's delegated
    // handler) and removed itself. Re-arm so the next click works too. The
    // short delay lets the current click finish before the loader re-runs.
    setTimeout(armBeehiivPopup, 50);
  }, []);

  return (
    <button
      type="button"
      id={CONTACT_US_TRIGGER_ID}
      onClick={handleClick}
      className={`inline-flex items-center gap-2 px-8 py-4 bg-SLIGreen hover:bg-green-700 text-SLIWhite font-semibold rounded transition-colors text-base ${className}`}
    >
      {children}
      <ArrowRight size={18} />
    </button>
  );
};

ContactUsButton.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export { ContactUsButton };
